/**
 * The copy and the small decisions §9 to §12 make about the document workspace, kept out of
 * the components so that a sentence the spec gives verbatim can be pinned by a test that
 * reads like the paragraph it came from.
 *
 * **Nothing here restates a rule that lives in `@hockeytd/shared`.** Which moves a batch may
 * make is `HEARING_DOCUMENT_TRANSITIONS`; whether a batch is open is `hearingBatchGate`; what
 * a state is called is `HEARING_DOCUMENT_STATE_LABELS`, or — in the minutes workspace bar —
 * `HEARING_MINUTES_WORKSPACE_LABELS`. This file names the BUTTONS §9's table names, and maps
 * each to the move the lattice already admits, so a rename here can never widen what the
 * screen offers.
 *
 * British English throughout.
 */

import {
  HEARING_DOCUMENT_STATE_LABELS,
  HEARING_MINUTES_WORKSPACE_LABELS,
  composeDecisionText,
  hearingBatchGate,
  isHearingDocumentTransitionLegal,
  type DocumentCase,
  type DocumentDecision,
  type HearingAttitude,
  type HearingBatch,
  type HearingDocumentState,
  type HearingDocumentStates,
  type HearingSanction,
  type Seriousness,
} from "@hockeytd/shared";

import type { HearingCaseDetailView, HearingChargeView } from "../../api/types.js";

/* ────────────────────────────────────────────────────────────────────────────
 * The paper (§9)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * §2's `EVENT`, which §9 puts across the top of every generated document — **read from the
 * tournament record, no longer typed here.**
 *
 * It is a Configuration §01 item, `Full Name For Letterheads`, and it sat in this file as the
 * constant `COMPETITION_FULL_NAME` while nothing on this page had a configuration read to
 * take it from. That was the least defensible of the fixed strings in the front end: it went
 * across the top of a HEARING LETTER and a set of MINUTES — documents that name a player and
 * a charge, and are served on a Team Manager — so on any tournament but one, the paper
 * asserted the wrong event over true findings.
 *
 * The name now comes from `useTournament()`, which is the `tournament` row as `GET /auth/me`
 * reports it. **A null tournament renders no event line at all**, and that is the honest
 * output rather than a degraded one: the letterhead caption already says in as many words
 * that branding is a per-tournament item awaiting configuration, and a document that named an
 * event the installation does not have would be the defect this replaced.
 *
 * §9's letterhead rule is unchanged and is the reason this is one helper: one name, applied
 * by the renderer, never retyped into a body.
 */
export function letterheadEventName(
  tournament: { fullNameForLetterheads: string } | null,
): string | null {
  return tournament?.fullNameForLetterheads ?? null;
}

/** §9: the branding caption, which says what the two logo plates are waiting for. */
export const LETTERHEAD_CAPTION =
  "Branding, letterhead logos and the full competition name are a per-tournament " +
  "configuration item, set once in Tournament Configuration and applied to every generated " +
  "output. The two plates above are placeholders until that item is configured.";

/**
 * The tournament's own zone, so `Tue 23 Sep` is the day it was in Nagoya.
 *
 * **RUN 83 — re-exported from `app/tournamentTime.ts`** rather than declared a second time here;
 * see the note on the same name in `appointments/vocabulary.ts`.
 */
export { TOURNAMENT_TIMEZONE } from "../../app/tournamentTime.js";

/**
 * What this build has instead of §9's `✓ .doc — Record Copy` and `✓ PDF — Sent To Team
 * Manager` chips.
 *
 * There is no `.doc` writer and no PDF pipeline anywhere in this system, and no message is
 * sent to anybody: the document is printed from the browser, off the read-only view, and the
 * TD saves or sends the result. A tick beside the words "sent to Team Manager" would be a
 * claim about a delivery that did not happen, on the one screen whose whole purpose (§1) is
 * that a document says only true things.
 */
export const OUTPUT_HONESTY_NOTE =
  "No .doc and no PDF file is produced, and nothing is sent to anybody from here. Use Print " +
  "and the browser's own print dialogue to put this document on paper or to save it as a " +
  "PDF, then file and send that copy yourself.";

/**
 * The whole of this system's output path: the browser's own print dialogue, over the
 * `@media print` block in `editorial.css`.
 *
 * Guarded because `window.print` is absent under jsdom, and a screen whose Print button
 * throws in a test is a screen nobody can hold to §9's rendering rule.
 */
export function printDocument(): void {
  if (typeof window !== "undefined" && typeof window.print === "function") window.print();
}

/** §9's AI-draft banner, verbatim, with the batch's own noun filled in. */
export function aiDraftBanner(batch: HearingBatch): string {
  const kind = batch === 1 ? "hearing letters" : "decision letters";
  return (
    `Generated from the case record and Alfred's reference ${kind}. Edit before issuing — ` +
    "the TD's wording is the wording that goes out."
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * The workspace bar (§10, §11, §12)
 * ──────────────────────────────────────────────────────────────────────────── */

const WORKSPACE_NOUN: Readonly<Record<HearingBatch, string>> = {
  1: "Hearing Letter",
  2: "Meeting Minutes",
  3: "Decision Letter",
};

/**
 * The bar's own title.
 *
 * §10 and §12 give two spellings each — `— Editable Draft` and `— Issued Document`. §11 gives
 * two of its own: the form is `— Pre-Defined Form` while it is open and `— Filed Record` once
 * it is filed.
 */
export function workspaceTitle(batch: HearingBatch, state: HearingDocumentState): string {
  const head = `Batch ${String(batch)} · ${WORKSPACE_NOUN[batch]} — `;
  if (batch === 2) return `${head}${state === "filed" ? "Filed Record" : "Pre-Defined Form"}`;
  return `${head}${state === "issued" ? "Issued Document" : "Editable Draft"}`;
}

/** The bar's chip. §11 spells the minutes' `pending` `Not Yet Filed`, and only there. */
export function workspaceChip(batch: HearingBatch, state: HearingDocumentState): string {
  return batch === 2
    ? HEARING_MINUTES_WORKSPACE_LABELS[state]
    : HEARING_DOCUMENT_STATE_LABELS[state];
}

/** §10 and §12's primary button, and §11's. */
export const WORKSPACE_PRIMARY: Readonly<Record<HearingBatch, string>> = {
  1: "Print",
  2: "Submit",
  3: "Save",
};

/**
 * The caption under the primary button — §10, §11 and §12, verbatim except that the `.doc`
 * and PDF promises are told the truth about.
 *
 * §10 reads *"Print produces the .doc record copy and the PDF sent to the Team Manager, and
 * marks the letter Issued"*, and §12 the same for Save. Neither file exists and nothing is
 * sent, so the half of each sentence that is true is kept and the half that is not is
 * replaced by what actually happens. §11's caption is unchanged, because filing the minutes
 * to the case is exactly what Submit does.
 */
export const WORKSPACE_PRIMARY_CAPTION: Readonly<Record<HearingBatch, string>> = {
  1:
    "Print stores your wording, marks the letter Issued, and opens the browser's print " +
    "dialogue on the issued document alone. No .doc and no PDF is filed, and nothing is sent " +
    "to the Team Manager from here.",
  2:
    "Submit files the minutes to the case. They are kept for any appeal and the decision " +
    "below flows into the Decision Letter.",
  3:
    "Save stores your wording and marks the decision Issued. No .doc and no PDF is filed, " +
    "and nothing is sent to the Team Manager or the person involved from here — print the " +
    "issued document to put it on paper.",
};

/** The read-only footer §10, §11 and §12 give the issued or filed document. */
export const WORKSPACE_ISSUED_NOTE: Readonly<Record<HearingBatch, string>> = {
  1: "Issued document — reopen it above to amend and reprint.",
  2:
    "Filed to the case and kept for any appeal. The decision above is what the Decision " +
    "Letter reuses.",
  3: "Issued and served — reopen it above to amend and re-save.",
};

/** The reopen control. §11's minutes reopen a FORM; §10 and §12 reopen a document. */
export function reopenLabel(batch: HearingBatch): string {
  return batch === 2 ? "Reopen Form" : "Reopen For Editing";
}

/* ────────────────────────────────────────────────────────────────────────────
 * §10d — re-drafting a letter from the case (ruled 2026-09-10)
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Whether the open workspace may offer §10d's `Re-Draft From The Case`.
 *
 * Two conditions, and no others.
 *
 *  1. **Batch 1 or batch 3 only.** §11's minutes are TYPED into a pre-defined form and their
 *     body is composed from what was typed; there is nothing to recompose from the case
 *     record, so the control does not exist there at all.
 *  2. **`draft` only.** `POST …/documents/:batch/generate` recomposes freely while a document
 *     is `pending` or `draft`, and migration 029's trigger refuses it over an `issued` one. An
 *     issued letter is reopened first (§10's `Reopen For Editing`), which returns it to
 *     `draft` — and the control then appears, correctly, with no case of its own.
 */
export function canRedraft(batch: HearingBatch, state: HearingDocumentState): boolean {
  return batch !== 2 && state === "draft";
}

/** §10d's affordance, in the Title Case the workspace bar's other actions use. */
export const REDRAFT_LABEL = "Re-Draft From The Case";

/** The confirm strip's kicker. */
export const REDRAFT_CONFIRM_TITLE = "Re-Draft From The Case · Your Wording Will Be Discarded";

/**
 * What the second press actually costs, said plainly.
 *
 * §10's banner promises *"the TD's wording is the wording that goes out"*, so recomposing a
 * draft destroys the one version of the letter the TD has vouched for. A control that did
 * that on a single press would be the failure mode that promise exists to prevent, so the
 * press is armed first and the box says what is lost before it is lost.
 */
export const REDRAFT_CONFIRM_BODY =
  "Re-drafting composes this letter again from the case record as it stands now. Everything " +
  "you have written or changed in this draft is discarded — including edits you have already " +
  "saved. Nothing else moves: the letter stays a draft and is not issued.";

/** The two buttons, in the house shape — the safe one first, the destructive one named. */
export const REDRAFT_CONFIRM_KEEP = "Keep My Wording";
export const REDRAFT_CONFIRM_GO = "Re-Draft Anyway";

/** The receipt, which states the loss rather than congratulating anybody. */
export const REDRAFT_RECEIPT =
  "Re-Drafted From The Case Record · Your previous wording is gone — the letter below is " +
  "freshly composed. It is still a draft and has not been issued.";

/**
 * What the panel says after a primary action, in place of §10's and §12's toasts.
 *
 * §10's toast reads *".doc filed to the tournament record and PDF sent to {manager}"* and
 * §12's the same. Both describe a file pipeline that does not exist and a delivery that does
 * not happen, so what is announced here is the move that was actually made.
 */
export function issuedReceipt(batch: HearingBatch, caseNumber: string): string {
  if (batch === 1) {
    return `Letter Issued · Recorded against case ${caseNumber}. Print it to put it on paper.`;
  }
  if (batch === 2) {
    return `Minutes Filed · Saved to case ${caseNumber} and kept for any appeal.`;
  }
  return `Decision Saved · Issued and recorded against case ${caseNumber}.`;
}

/* ────────────────────────────────────────────────────────────────────────────
 * §9's three cards
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * What one card offers, named as §9's table names it and mapped to the move it makes.
 *
 * `generate` composes the document and then advances `pending → draft` (or, for the minutes,
 * opens the form that will end in `pending → filed`). `open` opens the workspace on a
 * document that already exists and makes no move at all. `close` is §6's triage exit.
 */
export interface CardAction {
  kind: "generate" | "open" | "close";
  label: string;
  /** §9: `Draft Letter` is solid when the batch is Pending. */
  solid: boolean;
}

const GENERATE_LABEL: Readonly<Record<HearingBatch, string>> = {
  1: "Draft Letter",
  2: "Create Minutes",
  3: "Draft Decision",
};

const OPEN_LABEL: Readonly<Record<HearingBatch, string>> = {
  1: "Open Letter",
  2: "Open Minutes",
  3: "Open Decision",
};

/**
 * The buttons a card draws, and the gate message when it draws none.
 *
 * **Every action returned corresponds to a move the shared lattice admits**, and a gated card
 * returns nothing at all — §18's `Gated` row in as many words, *"Batch 2 and Batch 3 show
 * plain text instead of a button until their predecessor completes"*. `hearingBatchGate`
 * waves `not_required` through even while the pipeline move is shut, because that is how the
 * API lets a case be closed; the card still draws nothing beside a gate message, and closing
 * is taken on batch 1, which is never gated.
 */
export function cardActions(
  batch: HearingBatch,
  documents: HearingDocumentStates,
): { actions: CardAction[]; gate: string | null } {
  const from = documents[batch];

  // A document that exists can always be opened, and opening moves nothing.
  if (from === "draft") {
    return { actions: [{ kind: "open", label: "Continue Draft", solid: false }], gate: null };
  }
  if (from === "issued" || from === "filed") {
    return { actions: [{ kind: "open", label: OPEN_LABEL[batch], solid: false }], gate: null };
  }
  if (from === "not_required") return { actions: [], gate: null };

  // `pending`: the generate move, and — on batch 1 — §6's exit from the pipeline.
  const to: HearingDocumentState = batch === 2 ? "filed" : "draft";
  const refusal = hearingBatchGate(batch, from, to, documents);
  if (refusal !== null) return { actions: [], gate: refusal };
  if (!isHearingDocumentTransitionLegal(batch, from, to)) return { actions: [], gate: null };

  const actions: CardAction[] = [{ kind: "generate", label: GENERATE_LABEL[batch], solid: true }];
  if (batch === 1 && isHearingDocumentTransitionLegal(batch, from, "not_required")) {
    actions.push({ kind: "close", label: "Close Without A Hearing", solid: false });
  }
  return { actions, gate: null };
}

/** True when the case was closed as an informal enquiry — §6's middle triage path. */
export function isClosedCase(documents: HearingDocumentStates): boolean {
  return documents[1] === "not_required";
}

/* ────────────────────────────────────────────────────────────────────────────
 * §11's minutes form
 * ──────────────────────────────────────────────────────────────────────────── */

/** §11's pre-fill banner, verbatim. */
export const MINUTES_PREFILL_BANNER =
  "Everything that can carry forward from the case and the Hearing Letter is already here, " +
  "and the formulaic sentences are composed for you. Type only the time range, the testimony " +
  "narrative, the decision picks, the history and any penalty already incurred.";

/**
 * §11's attendee role dropdown — **amended 2026-09-24, after the TD chaired two hearings.**
 *
 * His words: *"The dropdown bar options for attendance, should also include, Technical Delegate
 * (Instead of Event Delegate), Team Manager, Team Coach, Team Assistant Manager, Team Assistant
 * Coach."* Two things follow from that, and both are here.
 *
 *  1. **`Event Delegate` is gone and `Technical Delegate` stands in its place.** An Event
 *     Delegate is not somebody who sits in a Code of Conduct hearing at this level of the game,
 *     and the one person who is always in the room — the chair — had no entry of his own. A
 *     dropdown that omits the chair and offers a role nobody present holds is a dropdown that
 *     pushes every real hearing onto `Other`.
 *  2. **The team side is spelled out.** `Team Official` was doing the work of four different
 *     people. Who came from the team — the manager, an assistant manager, the coach, an
 *     assistant coach — is exactly the fact the attendance block exists to record, because the
 *     hearing may well have been attended by a deputy rather than the manager named on the
 *     case. `Team Official` stays for the person who is none of those four.
 *
 * `Other` stays last and stays the escape hatch: a medical officer, a chaperone, anyone the
 * list does not name is recorded by name with `Other` beside it rather than not recorded.
 * {@link ATTENDEE_ROLES} is the single vocabulary — the minutes form's pre-filled rows compose
 * their roles from these spellings, so a seeded row and a hand-picked row read identically in
 * the filed document.
 */
export const ATTENDEE_ROLES: readonly string[] = [
  "Minutes Secretary",
  "Technical Delegate",
  "Assistant Technical Delegate",
  "Technical Officer For The Match",
  "Umpire",
  "Umpires Manager",
  "FIH Sport Manager",
  "AHF Sport Manager",
  "Team Manager",
  "Team Assistant Manager",
  "Team Coach",
  "Team Assistant Coach",
  "Team Official",
  "Player",
  "Interpreter",
  "Witness",
  "Other",
];

/** §11: six rows for everyone else in the room. */
export const ATTENDEE_ROW_COUNT = 6;

export const ATTENDEE_HINT =
  "These rows are the record of who was actually in the room, and they are the whole list the " +
  "Minutes and the Decision Letter print. The Team Manager, the persons involved and the chair " +
  "are filled in for you — correct them, and clear the name of anyone who did not attend. Add " +
  "the person who took the minutes and mark that row Minutes Secretary; the signature block is " +
  "composed from it.";

/**
 * §11's signature line: `{secretary name} / Minutes Secretary / {date} 2026`, and with no row
 * marked, `— mark a row as Minutes Secretary —` in the name's place, updating live.
 */
export function minutesSignatureLine(secretaryName: string | null, dayText: string | null): string {
  const who =
    secretaryName === null || secretaryName.trim() === ""
      ? "— mark a row as Minutes Secretary —"
      : secretaryName.trim();
  return `${who} / Minutes Secretary / ${dayText ?? ""} 2026`.replace(/\s+$/, "");
}

/**
 * The composed-decision hint.
 *
 * §11's own wording is *"Composed decision — edit freely, the wording below is what is filed
 * and what the Decision Letter reuses"*. The second half is true and is kept; the first half
 * is not, and is not. `composeDecisionText` in `@hockeytd/shared` is what both the minutes and
 * the Decision Letter are composed by, on the server, from the picks that `PUT
 * /hearings/:id/minutes` carries — there is no field on that route for an edited decision
 * paragraph, so a textarea here would take typing and throw it away. Changing a pick changes
 * the wording, in both documents, which is exactly the drift §11 exists to prevent.
 */
export const COMPOSED_DECISION_HINT =
  "Composed decision — this is composed from your picks above and recomposes as you change " +
  "them. It is the wording that is filed and the wording the Decision Letter reuses, so " +
  "change a pick rather than the sentence.";

/** §11's testimony hint, verbatim. */
export const TESTIMONY_HINT =
  "The first sentence is generated from the charges on this case. Everything after it is yours.";

/* ────────────────────────────────────────────────────────────────────────────
 * Composing the decision, without restating how it is composed
 * ──────────────────────────────────────────────────────────────────────────── */

/** The picks §11's segmented controls hold, before they are saved. */
export interface DecisionPicks {
  breachFound: boolean;
  seriousness: Seriousness;
  sanction: HearingSanction;
  matchesSuspended: number;
  conditionalEscalation: boolean;
  referUpward: boolean;
  severityNarrative: string;
  acceptsBreach: boolean;
  attitude: HearingAttitude;
  disciplinaryHistory: string;
  penaltyAlreadyIncurred: string;
}

/**
 * The case, as `composeDecisionText` sees it.
 *
 * Only the persons involved and the charges are read by that function, and everything else is
 * filled from the record or left null — deliberately, because the alternative is a second
 * copy of §11's composition rules living in the browser, and §11's whole point is that the
 * minutes and the Decision Letter cannot drift apart.
 */
export function asDocumentCase(hearing: HearingCaseDetailView): DocumentCase {
  return {
    caseNumber: hearing.caseNumber,
    teamCode: hearing.teamCode,
    teamManagerName: hearing.teamManagerName,
    personsInvolved: hearing.personsInvolved.map((person) => ({
      name: person.name,
      role: person.role,
      shirtNumber: person.shirtNumber,
      gender: person.gender === "f" ? "f" : "m",
    })),
    charges: hearing.charges.map(asDocumentCharge),
    incidentDetails: hearing.incidentDetails,
    match: null,
    opponentTeam: null,
    matchPhase: hearing.matchPhase,
    matchMinute: hearing.matchMinute === null ? null : String(hearing.matchMinute),
    hearingAt: hearing.hearingAt,
    hearingLocation: hearing.hearingLocation,
    isLateReport: hearing.isLateReport,
    lateReasons: hearing.lateReasons,
    hasVideoEvidence: false,
    inAbsentia: hearing.inAbsentia,
    absentNote: hearing.absentNote,
  };
}

export function asDocumentCharge(charge: HearingChargeView): {
  article: string;
  level: 1 | 2 | 3;
  articleText: string;
  seriousness: Seriousness;
} {
  return {
    article: charge.article,
    level: charge.level,
    articleText: charge.articleText,
    seriousness: charge.seriousness,
  };
}

/** The picks, as `composeDecisionText` and `PUT /hearings/:id/minutes` see them. */
export function asDocumentDecision(picks: DecisionPicks): DocumentDecision {
  return {
    breachFound: picks.breachFound,
    acceptsBreach: picks.acceptsBreach,
    finding: "",
    reasoning: "",
    seriousness: picks.seriousness,
    attitude: picks.attitude,
    disciplinaryHistory: picks.disciplinaryHistory.trim() === "" ? null : picks.disciplinaryHistory,
    penaltyAlreadyIncurred:
      picks.penaltyAlreadyIncurred.trim() === "" ? null : picks.penaltyAlreadyIncurred,
    sanction: picks.sanction,
    matchesSuspended: picks.sanction === "suspension" ? picks.matchesSuspended : null,
    conditionalEscalation: picks.conditionalEscalation,
    referUpward: picks.referUpward,
    severityNarrative: picks.severityNarrative.trim() === "" ? null : picks.severityNarrative,
    suspendedMatches: [],
    suspensionCannotBeServedInFull: false,
  };
}

/** §11's live composition, delegated whole to the shared composer. */
export function composeDecisionPreview(
  hearing: HearingCaseDetailView,
  picks: DecisionPicks,
): string {
  return composeDecisionText(asDocumentCase(hearing), asDocumentDecision(picks));
}
